import apolloClient from 'api/apolloClient';
import {
  CREATE_GROUP,
  GROUP_BY_ID,
  SAVE_NOTES,
  UPDATE_GROUP,
  UPDATE_AGENT_OF_RECORD_AND_ASSIGNEES,
  UPDATE_RESPONSIBLE_PARTIES,
} from 'api/queries/groupService';
import { action, makeObservable, observable, runInAction } from 'mobx';
import {
  AccountRepresentative,
  Assignee,
  AssigneeType,
  BiGroup,
  Group,
  GroupSource,
  InputGroup,
} from 'types/groupService';

interface UpdateGroupAgentsProps {
  agentOfRecord?: Assignee;
  assigned?: Assignee[];
  accountRepresentative?: AccountRepresentative;
}

interface UpdateAgentReturnType {
  id: string;
  agentOfRecord: Assignee;
}

interface UpdateAccountRepReturnType {
  id: string;
  accountRepresentative: AccountRepresentative;
}

interface UpdateAssigneesReturnType {
  id: string;
  assigned: Assignee[];
}

export const INITIAL_GROUP_OBJECT: Group = Object.freeze({
  id: '',
  name: '',
  source: GroupSource.AGENT_V2,
  browsingCriteria: {
    effectiveDate: '',
    zipCode: '',
    state: '',
    county: '',
  },
  contactInfo: {
    firstName: '',
    lastName: '',
    primaryEmail: '',
    title: '',
    primaryPhone: '',
    primaryAddress: {
      city: '',
      street: '',
      street2: '',
      zip: '',
      state: '',
      county: '',
    },
  },
  creationDateTime: '',
  assigned: [],
  agentOfRecord: {
    id: '',
    name: '',
    npn: '',
    tin: '',
    email: '',
    type: AssigneeType.AGENT,
  },
  accountRepresentative: null,
  lastUpdateTime: '',
  notes: '',
  archived: false,
  additionalAORs: [],
  additionalContactInfos: [],
  seUserRole: 'BROKER',
});

export const INITIAL_BI_GROUP_OBJECT: BiGroup = Object.freeze({
  ...INITIAL_GROUP_OBJECT,
  agency: { id: '', name: '' },
  agentOfRecord: {
    id: '',
    name: '',
    firstName: '',
    lastName: '',
    npn: '',
    tin: '',
    email: '',
    type: AssigneeType.AGENT,
  },
});

export class GroupStore {
  group: Group = {
    ...INITIAL_GROUP_OBJECT,
  };

  /**
   *  @OBSOLETE: use getGroupById from /operations instead!
   */
  async getGroupById(id: string): Promise<void> {
    const res = await apolloClient.query<{ groupById: Group }>({
      query: GROUP_BY_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    runInAction(() => {
      this.group = res.data.groupById;
    });
  }

  async createGroup(inputGroup: InputGroup): Promise<void> {
    const res = await apolloClient.mutate<{ createGroup: Group }>({
      mutation: CREATE_GROUP,
      variables: { inputGroup },
    });
    runInAction(() => {
      if (res.data) {
        this.group = res.data.createGroup;
      }
    });
  }

  async updateGroup(inputGroup: InputGroup): Promise<void> {
    const res = await apolloClient.mutate<{ updateGroup: Group }>({
      mutation: UPDATE_GROUP,
      variables: { inputGroup },
    });
    runInAction(() => {
      if (res.data) {
        this.group = res.data.updateGroup;
      }
    });
  }

  async saveNotesToGroup(groupId: string, notes: string): Promise<void> {
    const res = await apolloClient.mutate<{ saveNotes: Group }>({
      mutation: SAVE_NOTES,
      variables: { groupId, notes },
    });
    runInAction(() => {
      if (res.data) {
        this.group.notes = res.data.saveNotes.notes;
      }
    });
  }

  public async saveAgentsAndAssignees(
    selectedAgentId: string,
    assignedAssignees: Assignee[],
    newAccRep: AccountRepresentative | null,
  ): Promise<void> {
    const mutation = newAccRep ? UPDATE_RESPONSIBLE_PARTIES : UPDATE_AGENT_OF_RECORD_AND_ASSIGNEES;

    const res = await apolloClient.mutate<{
      saveAssignee: UpdateAssigneesReturnType;
      updateAgentOfRecord: UpdateAgentReturnType;
      updateAccountRepresentative?: UpdateAccountRepReturnType;
    }>({
      mutation,
      variables: {
        groupId: this.group.id,
        agentId: selectedAgentId,
        assignees: assignedAssignees.map((assignee) => assignee.id),
        ...(newAccRep && { newAccRep }), // Only include newAccRep if it's not null
      },
    });

    runInAction(() => {
      if (res.data) {
        this.updateGroupAgents({
          agentOfRecord: res.data.updateAgentOfRecord.agentOfRecord,
          assigned: res.data.saveAssignee.assigned,
          accountRepresentative: res.data.updateAccountRepresentative?.accountRepresentative,
        });
      }
    });
  }

  private updateGroupAgents(params: UpdateGroupAgentsProps): void {
    if (params.agentOfRecord) {
      this.group.agentOfRecord = params.agentOfRecord;
    }
    if (params.assigned) {
      this.group.assigned = params.assigned;
    }
    if (params.accountRepresentative) {
      this.group.accountRepresentative = params.accountRepresentative;
    }
  }

  constructor() {
    makeObservable(this, {
      group: observable,
      getGroupById: action.bound,
      createGroup: action.bound,
      updateGroup: action.bound,
      saveAgentsAndAssignees: action.bound,
    });
  }
}
